import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { Repeater, RepeaterTitle } from './repeater';
import { ImageUploadField } from './image-upload-field';

type TestimonialsBlock = Extract<Block, { type: 'testimonials' }>;
type TestimonialsProps = TestimonialsBlock['props'];
type Item = TestimonialsProps['items'][number];

function newTestimonial(): Item {
  return {
    quote: 'They were fast, fair, and the work has held up perfectly.',
    author: 'New customer',
    role: '',
    avatar: { url: '', alt: '' },
  };
}

interface TestimonialsInspectorProps {
  block: TestimonialsBlock;
  onChange: (next: TestimonialsProps) => void;
}

export function TestimonialsInspector({ block, onChange }: TestimonialsInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<TestimonialsProps>) => onChange({ ...props, ...next });

  return (
    <>
      <InspectorSection>
        <InspectorField
          label="Title"
          value={props.title ?? ''}
          placeholder="What our customers say"
          onChange={(v) => patch({ title: v })}
        />
      </InspectorSection>

      <InspectorSection label="Testimonials">
        <Repeater
          items={props.items}
          onChange={(items) => patch({ items: items as TestimonialsProps['items'] })}
          addLabel="Add testimonial"
          makeNew={newTestimonial}
          renderTitle={(item) => <RepeaterTitle>{item.author}</RepeaterTitle>}
          renderForm={(item, _i, update) => (
            <>
              <InspectorField
                label="Quote"
                value={item.quote}
                multiline
                rows={3}
                placeholder="They showed up on time and the work has held up perfectly."
                onChange={(v) => update({ quote: v })}
              />
              <InspectorField
                label="Author"
                value={item.author}
                placeholder="Maria S."
                onChange={(v) => update({ author: v })}
              />
              <InspectorField
                label="Role (optional)"
                value={item.role ?? ''}
                placeholder="Homeowner, Pacific Beach"
                onChange={(v) => update({ role: v })}
              />
              <ImageUploadField
                label="Avatar (optional)"
                url={item.avatar?.url ?? ''}
                onChange={(url) =>
                  update({ avatar: { url, alt: item.avatar?.alt ?? '' } })
                }
              />
              <InspectorField
                label="Avatar alt (optional)"
                value={item.avatar?.alt ?? ''}
                placeholder={`Headshot of ${item.author || 'the author'}`}
                helperText="Describe the avatar for screen readers"
                onChange={(alt) =>
                  update({ avatar: { url: item.avatar?.url ?? '', alt } })
                }
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
